/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import Container from 'react-bootstrap/Container';
import { Row, Col } from 'react-bootstrap';
import { useTheme } from 'styled-components';
import { useEffect, useState, useContext } from 'react';
import { getAiOps } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { useNavigate } from 'react-router-dom';
import { useRef, memo, useMemo } from 'react';
import VisGraph, {
  GraphData,
  GraphEvents,
  Options,
} from 'react-vis-graph-wrapper';

function AiOpsGraph({ }) {
    const theme : any = useTheme();
    const [data, setData] = useState<GraphData>(null as any);
    const statusContext = useContext<StatusContextType>(StatusContext);
    const navigate = useNavigate();
    const networkRef = useRef<any>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let rawOps = await getAiOps();
                let graphData: GraphData = {
                    nodes: [],
                    edges: []
                };
                let idno = 1000000;
                for (let op of rawOps) {
                    graphData.nodes.push({ id: op.id, label: op.id, title: op.description });

                    let m = null;
                    let rfunv = /(runAiProbe|runDnsProbe|runTcpProbe|runCodeProbeFromTemplate)\('?([^,']+)'?/g
                    while ((m = rfunv.exec(op.impl)) !== null) {
                        let fid = m[2];
                        switch (m[1]) {
                        case 'runAiProbe':
                            graphData.edges.push({ from: op.id, to: fid, arrows: 'to' });
                            break;
                        case 'runDnsProbe':
                            graphData.nodes.push({ id: idno, label: `DNS: ${fid}` });
                            graphData.edges.push({ from: op.id, to: idno, arrows: 'to' });
                            idno++;
                            break;
                        case 'runTcpProbe':
                            graphData.nodes.push({ id: idno, label: `TCP: ${fid}` });
                            graphData.edges.push({ from: op.id, to: idno, arrows: 'to' });
                            idno++;
                            break;
                        case 'runCodeProbeFromTemplate':
                            graphData.nodes.push({ id: idno, label: `CODE: ${fid}` });
                            graphData.edges.push({ from: op.id, to: idno, arrows: 'to' });
                            idno++;
                            break;
                        }
                    }
                }

                // root node and connections
                graphData.nodes.push({ id: 'all', label: 'All', fixed: true, title: 'All Operations', font: { color: theme.colors.nodeRoot }, color: { border: theme.colors.nodeRoot } });
                let opIds = new Set(rawOps.map(o => o.id));
                for (let e of graphData.edges) {
                    if (opIds.has(e.to as string)) {
                        opIds.delete(e.to as string);
                    }
                }
                // @ts-ignore
                for (let oid of opIds) {
                    graphData.edges.push({ from: 'all', to: oid, arrows: 'to' });
                }
                setData(graphData);
            } catch (error: any) {
                statusContext.postError(`Error fetching AI Operations: ${error.message}`);
            }
        };
        fetchData();
    }, []);

    const options = {
        layout: {
        },
        physics: {
            enabled: true,
            barnesHut: {
                gravitationalConstant: -1000,
                avoidOverlap: 0.3,
            },
            wind: { x: 0,
                    y: 0 },
        },
        edges: {
            color: theme.colors.ctext,
//            length: 100,
        },
        interaction: {
            tooltipDelay: 1000,
        },
        nodes: {
            shape: 'box',
            font: {
                color: theme.colors.ctext
            },
            color: {
                background: theme.colors.bg,
                border: theme.colors.ctext,
                highlight: { background: theme.colors.bg, border: theme.colors.ctext },
                hover: { background: theme.colors.bg, border: theme.colors.ctext }
            }
        }
    };

    const events: GraphEvents = {
        select: (event: any) => {
            const { nodes, edges } = event;
            if (nodes.length > 0) {
                const opId = nodes[0];
                if (opId !== 'all' && !String(opId).startsWith('CODE:') && !String(opId).startsWith('DNS:') && !String(opId).startsWith('TCP:')) {
                    navigate(`/ai/edit_op/${opId}`);
                }
            }
        },
    }

    if (!data) return <div>Loading...</div>;

    return (
        <div style={{ backgroundColor: theme.colors.mapBg, color: theme.colors.ctext, height: '820px', width: '100%' }}>
                    <VisGraph key="vg3" graph={data} options={options} events={events} 
                     ref={(network) => {
                        networkRef.current = network;
                        if (networkRef.current) {
                            networkRef.current.once('stabilized', () => {
                                networkRef.current.fit();
                            });
                        }
                     }}
                    />
        </div>
    );
}

export default AiOpsGraph;
/*

                     ref={(network) => {
                        networkRef.current = network;
                        if (networkRef.current) {
                            networkRef.current.once('stabilized', () => {
                                networkRef.current.fit();
                            });
                        }
                     }}
*/